import Endpoints from 'constants/endpoints';
import focusBearApi, { isAxiosError } from 'services/axios-config';
import { validateImageFile } from './file-validation';

/**
 * Activity images (private R2): authenticated API calls use `focusBearApi` (JWT).
 * The presigned `uploadUrl` PUT must NOT send the app JWT — use direct XHR/fetch to R2 only.
 */

/** POST /activity/:activityId/images/generate-upload-url — JSON body (snake_case). */
export interface GenerateActivityImageUploadUrlRequest {
  file_name: string;
  content_type: string;
}

/** Response JSON (camelCase); parser also accepts snake_case variants. */
export interface GenerateActivityImageUploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
}

/** GET /activity/:activityId/images?file_key=… — response (camelCase). */
export interface GetActivityImageReadUrlResponse {
  readUrl: string;
  expiresInSeconds: number;
}

export interface ActivityImageUploadResult {
  fileKey: string;
}

export type ActivityImageReadUrlResult = GetActivityImageReadUrlResponse;

interface UploadToSignedUrlOptions {
  onProgress?: (progressPercent: number) => void;
}

/** Backend may return camelCase or snake_case. */
const parseActivityImageGenerateUploadResponse = (
  raw: Record<string, unknown>
) => {
  const uploadUrl =
    (typeof raw.uploadUrl === 'string' && raw.uploadUrl) ||
    (typeof raw.upload_url === 'string' && raw.upload_url) ||
    undefined;
  const fileKey =
    (typeof raw.fileKey === 'string' && raw.fileKey) ||
    (typeof raw.file_key === 'string' && raw.file_key) ||
    undefined;
  return { uploadUrl, fileKey };
};

const parseActivityImageReadResponse = (raw: Record<string, unknown>) => {
  const readUrl =
    (typeof raw.readUrl === 'string' && raw.readUrl) ||
    (typeof raw.read_url === 'string' && raw.read_url) ||
    '';
  const exp = raw.expiresInSeconds ?? raw.expires_in_seconds;
  let expiresInSeconds = 300;
  if (typeof exp === 'number' && !Number.isNaN(exp)) {
    expiresInSeconds = exp;
  } else if (typeof exp === 'string') {
    const parsed = parseInt(exp, 10);
    expiresInSeconds = Number.isNaN(parsed) ? 300 : parsed;
  }
  return { readUrl, expiresInSeconds };
};

export const deleteActivityImage = async (fileKey: string): Promise<void> => {
  await focusBearApi.delete(
    Endpoints.DELETE_ACTIVITY_IMAGE.replace(
      '{file_path}',
      encodeURIComponent(fileKey)
    )
  );
};

const mapReadUrlHttpError = (status: number | undefined): string => {
  if (status === 403) return 'image_upload.read_forbidden';
  if (status === 404) return 'image_upload.read_not_found';
  if (status === 400) return 'image_upload.read_bad_request';
  return 'image_upload.missing_read_url';
};

export const getActivityImageReadUrl = async (
  activityId: string,
  fileKey: string
): Promise<ActivityImageReadUrlResult> => {
  try {
    const { data } = await focusBearApi.get(
      `${Endpoints.ACTIVITY_IMAGES(activityId)}?file_key=${encodeURIComponent(fileKey)}`
    );
    const { readUrl, expiresInSeconds } = parseActivityImageReadResponse(
      (data ?? {}) as Record<string, unknown>
    );
    if (!readUrl) {
      throw new Error('image_upload.missing_read_url');
    }
    return { readUrl, expiresInSeconds };
  } catch (err: unknown) {
    if (isAxiosError(err) && err.response?.status) {
      throw new Error(mapReadUrlHttpError(err.response.status));
    }
    throw err instanceof Error
      ? err
      : new Error('image_upload.missing_read_url');
  }
};

const uploadToSignedUrl = (
  uploadUrl: string,
  file: File,
  options?: UploadToSignedUrlOptions
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (event: ProgressEvent<EventTarget>) => {
      if (
        !options?.onProgress ||
        !event.lengthComputable ||
        event.total === 0
      ) {
        return;
      }
      const percent = Math.round((event.loaded / event.total) * 100);
      options.onProgress(percent);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        options?.onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error('image_upload.upload_failed'));
    };

    xhr.onerror = () => reject(new Error('image_upload.upload_failed'));
    xhr.onabort = () => reject(new Error('image_upload.upload_failed'));
    xhr.send(file);
  });

export const uploadActivityImage = async (
  activityId: string,
  file: File,
  options?: UploadToSignedUrlOptions
): Promise<ActivityImageUploadResult> => {
  if (!activityId) {
    throw new Error('image_upload.failed_to_get_url');
  }

  const validation = await validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error ?? 'file_validation.invalid_type');
  }

  const body: GenerateActivityImageUploadUrlRequest = {
    file_name: file.name,
    content_type: file.type
  };
  const { data: uploadData } = await focusBearApi.post(
    Endpoints.ACTIVITY_IMAGE_GENERATE_UPLOAD_URL(activityId),
    body
  );

  const parsed = parseActivityImageGenerateUploadResponse(
    (uploadData ?? {}) as Record<string, unknown>
  );

  if (!parsed.uploadUrl || !parsed.fileKey) {
    throw new Error('image_upload.failed_to_get_url');
  }

  await uploadToSignedUrl(parsed.uploadUrl, file, options);

  return { fileKey: parsed.fileKey };
};
